import { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import { PrimaryBlackButton, PrimaryWhiteButton } from '../../../components/Button/Buttons';
import StatsCard from '../../../components/Cards/StartsCard';
import PageHeader from '../../../components/Layout/PageHeader';
import PageLayout from '../../../components/Layout/PageLayout';
import {
  Card,
  CardTitle,
  Hint,
  Input,
  Scroll,
  Select,
  SimpleTable,
  Stack,
  StatsGrid,
} from '../../../components/Layout/PagePieces';
import Page from '../../../components/Page/Page';
import useFindUser from '../../../hooks/dashboard/limits/useFindUser';
import useLimitSettings from '../../../hooks/dashboard/limits/useLimitSettings';
import useRefusalSummary from '../../../hooks/dashboard/limits/useRefusalSummary';
import useUserLimits from '../../../hooks/dashboard/limits/useUserLimits';
import { LIMIT_LABELS, OVERRIDABLE } from '../../../models/limits/limits.model';
import { formatNaira, tableFormatDateTimeNG } from '../../../utils/utils';
import LimitsReviewCard from './LimitsReviewCard';

const money = (v: string | number) => formatNaira(Number(v));
const COUNTS = ['NEW_ACCOUNT_DAYS', 'NEW_CARD_HOURS'];

// Spending limits: the defaults, one customer's limits and overrides, and
// today's refusals (limits spec L7). Absolute maxima are code settings only.
const Limits = () => {
  const { settings, saving, fetchSettings, save } = useLimitSettings();
  const { refusals, fetchRefusals } = useRefusalSummary();

  useEffect(() => {
    fetchSettings();
    fetchRefusals();
    // eslint-disable-next-line
  }, []);

  const keys = Object.keys(settings?.values ?? {});
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { ...Object.fromEntries(keys.map((k) => [k, Number(settings?.values[k] ?? 0)])), reason: '' } as Record<string, any>,
    validationSchema: Yup.object({ reason: Yup.string().min(10, 'Say why (at least 10 characters)').required('Say why') }),
    onSubmit: (v) => {
      const changed = Object.fromEntries(keys.filter((k) => Number(v[k]) !== Number(settings?.values[k])).map((k) => [k, Number(v[k])]));
      if (!Object.keys(changed).length) {
        toast('Nothing changed');
        return;
      }
      save(changed, v.reason, () => {
        toast.success('Limits saved');
        formik.setFieldValue('reason', '');
      });
    },
  });

  return (
    <Page title='Hethera | Limits'>
      <PageLayout>
        <Stack>
          <PageHeader title='Spending limits' className='borderless no-bottom-padding' />
          {settings && !settings.enabled && (
            <Off>Limits are switched off (LIMITS_ENABLED=false): nothing is refused yet. Numbers below apply once they’re on.</Off>
          )}

          <StatsGrid>
            <StatsCard.Root>
              <StatsCard.Header>Refused today</StatsCard.Header>
              <StatsCard.Value>{refusals?.total ?? 0}</StatsCard.Value>
              <StatsCard.PlainFooter>Nothing charged for any of these</StatsCard.PlainFooter>
            </StatsCard.Root>
            {Object.entries(refusals?.by_key ?? {})
              .slice(0, 3)
              .map(([key, count]) => (
                <StatsCard.Root key={key}>
                  <StatsCard.Header>{LIMIT_LABELS[key] ?? key}</StatsCard.Header>
                  <StatsCard.Value>{count}</StatsCard.Value>
                  <StatsCard.PlainFooter>refused today</StatsCard.PlainFooter>
                </StatsCard.Root>
              ))}
          </StatsGrid>

          <Card>
            <CardTitle>Defaults for every customer</CardTitle>
            <Hint>
              Per-purchase limits count the bill; daily and monthly count what the card is charged. New accounts and newly
              added cards get the lower numbers for a while.
              {settings?.updated_at ? ` Last changed ${tableFormatDateTimeNG(settings.updated_at)} by ${settings.updated_by}.` : ''}
            </Hint>
            <form onSubmit={formik.handleSubmit}>
              <Grid>
                {keys.map((key) => (
                  <Field key={key}>
                    <span>{LIMIT_LABELS[key] ?? key}</span>
                    <Input type='number' name={key} value={formik.values[key] ?? ''} onChange={formik.handleChange} min={0} />
                    <small>
                      {COUNTS.includes(key) ? '' : '₦ · '}
                      {settings?.absolute_max[key] ? `max ${money(settings.absolute_max[key])}` : `default ${settings?.file_defaults[key] ?? ''}`}
                    </small>
                  </Field>
                ))}
              </Grid>
              <Field>
                <span>Why are you changing them?</span>
                <Input name='reason' value={formik.values.reason} onChange={formik.handleChange} placeholder='e.g. Raising the daily limit after the first month' />
                {formik.errors.reason && formik.touched.reason && <ErrorText>{String(formik.errors.reason)}</ErrorText>}
              </Field>
              <Actions>
                <PrimaryBlackButton type='submit' disabled={saving}>
                  {saving ? 'Saving…' : 'Save defaults'}
                </PrimaryBlackButton>
              </Actions>
            </form>
          </Card>

          <LimitsReviewCard />

          <CustomerLimits />
        </Stack>
      </PageLayout>
    </Page>
  );
};

export default Limits;

const CustomerLimits = () => {
  const { finding, findUser } = useFindUser();
  const { limits, saving, fetchLimits, setOverride, removeOverride, clear } = useUserLimits();
  const [phone, setPhone] = useState('');
  const [user, setUser] = useState<{ id: string; chat_id: string; display_name?: string | null } | null>(null);
  const [form, setForm] = useState({ limit_key: 'DAILY', amount: '', reason: '', expires_at: '' });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!form.amount || form.reason.trim().length < 10) return toast.error('An amount and a reason (10+ characters) are needed');
    setOverride(
      user.id,
      {
        limit_key: form.limit_key,
        amount: Number(form.amount),
        reason: form.reason.trim(),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      },
      () => {
        toast.success('Limit changed for this customer');
        setForm({ ...form, amount: '', reason: '', expires_at: '' });
      }
    );
  };

  return (
    <Card>
      <CardTitle>One customer</CardTitle>
      <Hint>Raise a limit for a business customer, or lower one while you look into an account. Every change needs a reason and is kept.</Hint>
      <Row
        as='form'
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          clear();
          findUser(phone, (found) => {
            setUser(found);
            fetchLimits(found.id);
          });
        }}
      >
        <Input placeholder='WhatsApp number, e.g. 0803 123 4567' value={phone} onChange={(e) => setPhone(e.target.value)} />
        <PrimaryWhiteButton type='submit' disabled={finding || !phone}>
          {finding ? 'Finding…' : 'Find customer'}
        </PrimaryWhiteButton>
      </Row>

      {user && limits && (
        <>
          <strong>{user.display_name ?? user.chat_id}</strong>
          <SimpleTable>
            <tbody>
              {Object.entries(limits.per_purchase).map(([bill, amount]) => (
                <tr key={bill}>
                  <td>One {bill.toLowerCase().replace('_', ' ')} purchase</td>
                  <td className='num'>{money(amount)}</td>
                </tr>
              ))}
              <tr>
                <td>Per day</td>
                <td className='num'>
                  {money(limits.daily)} ({money(limits.spent_today)} used)
                </td>
              </tr>
              <tr>
                <td>Per month</td>
                <td className='num'>
                  {money(limits.monthly)} ({money(limits.spent_this_month)} used)
                </td>
              </tr>
              {limits.new_account_until && (
                <tr>
                  <td>New-account limits until</td>
                  <td className='num'>{tableFormatDateTimeNG(limits.new_account_until)}</td>
                </tr>
              )}
              {limits.new_card_until && (
                <tr>
                  <td>New-card limit ({money(limits.new_card_limit ?? 0)}) until</td>
                  <td className='num'>{tableFormatDateTimeNG(limits.new_card_until)}</td>
                </tr>
              )}
            </tbody>
          </SimpleTable>

          <Row as='form' onSubmit={submit}>
            <Select value={form.limit_key} onChange={(e) => setForm({ ...form, limit_key: e.target.value })}>
              {OVERRIDABLE.map((k) => (
                <option key={k} value={k}>
                  {LIMIT_LABELS[k]}
                </option>
              ))}
            </Select>
            <Input type='number' placeholder='₦ amount' value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            <Input placeholder='Reason' value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            <Input type='datetime-local' aria-label='Expires (optional)' value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
            <PrimaryBlackButton type='submit' disabled={saving}>
              Set limit
            </PrimaryBlackButton>
          </Row>

          {!!limits.overrides.length && (
            <Scroll>
              <SimpleTable>
                <thead>
                  <tr>
                    <th>Limit</th>
                    <th className='num'>Amount</th>
                    <th>Reason</th>
                    <th>Expires</th>
                    <th>State</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {limits.overrides.map((o) => (
                    <tr key={o.id}>
                      <td>{LIMIT_LABELS[o.limit_key] ?? o.limit_key}</td>
                      <td className='num'>{money(o.amount)}</td>
                      <td>{o.reason}</td>
                      <td>{o.expires_at ? tableFormatDateTimeNG(o.expires_at) : 'Never'}</td>
                      <td>{o.active ? 'Active' : o.revoked_at ? 'Removed' : 'Expired'}</td>
                      <td>
                        {o.active && (
                          <PrimaryWhiteButton
                            type='button'
                            onClick={() => {
                              const reason = window.prompt('Why remove this limit? (10+ characters)') ?? '';
                              if (reason.trim().length >= 10) removeOverride(user.id, o.id, reason.trim());
                            }}
                          >
                            Remove
                          </PrimaryWhiteButton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </SimpleTable>
            </Scroll>
          )}

          {!!limits.refusals.length && (
            <>
              <strong>Recent refusals</strong>
              <SimpleTable>
                <tbody>
                  {limits.refusals.map((r) => (
                    <tr key={r.id}>
                      <td>{tableFormatDateTimeNG(r.created_at)}</td>
                      <td>{LIMIT_LABELS[r.limit_key] ?? r.limit_key}</td>
                      <td className='num'>{money(r.attempted_amount)}</td>
                      <td>{r.source}</td>
                    </tr>
                  ))}
                </tbody>
              </SimpleTable>
            </>
          )}
        </>
      )}
    </Card>
  );
};

const Off = styled.div`
  padding: 12px 16px;
  border-radius: 12px;
  background: #fff8e6;
  border: 1px solid #f1d48a;
  color: #6b4a00;
  font-size: 14px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.grayTextColor};

  small {
    font-size: 11px;
  }
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.palette.errorRed};
  font-size: 12px;
`;

const Actions = styled.div`
  margin-top: 12px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;

  & > input {
    flex: 1 1 160px;
  }
`;
